// Regelwerk (FR-05, FR-41): alles, was der Kunde einstellen können soll, statt es im Code zu haben.
//
// Gespeichert in `rule_sets.rules` (jsonb), versioniert über `valid_from`. Für einen Tag gilt die
// Version mit dem jüngsten valid_from <= Tag. Fehlende Felder bekommen die Standardwerte — so bleibt
// eine alte Version gültig, wenn später ein neues Feld dazukommt.

import { isLocalDate, type LocalDate } from "./time.ts";

export interface TimeRules {
  /** Ab hier ist die Pflichtpause fällig: Arbeitszeit MEHR als diese Minuten (FR-05, „ab 6 Stunden"). */
  breakThresholdMinutes: number;
  /** Dauer der Pflichtpause. Im Lastenheft offen („[Dauer ergänzen]") — Standard 30. */
  requiredBreakMinutes: number;
  /** Kürzere Lücken zwischen Gehen und Kommen zählen nicht als Pause (aber auch nicht als Arbeit). */
  minBreakSegmentMinutes: number;
  /** Zweites Tippen desselben Mitarbeiters innerhalb dieser Sekunden = versehentlich doppelt. */
  duplicateWindowSeconds: number;
  /** FR-07: fehlende Pflichtpause beim Tagesabschluss automatisch abziehen. */
  autoBreakDeduction: boolean;
  /**
   * Abzug höchstens bis auf die Schwelle: 6:10 ohne Pause ⇒ 10 Minuten statt 30. Standard AUS,
   * weil das Lastenheft „Pflichtpause wird eingetragen" sagt.
   */
  capAutoBreakAtThreshold: boolean;
  /** FR-06: Ortszeit der Erinnerung. */
  breakReminderTime: string;
  /** FR-07/FR-43: ab dieser Ortszeit wird der Vortag abgeschlossen. */
  dayCloseTime: string;
}

export const DEFAULT_RULES: TimeRules = {
  breakThresholdMinutes: 360,
  requiredBreakMinutes: 30,
  minBreakSegmentMinutes: 10,
  duplicateWindowSeconds: 60,
  autoBreakDeduction: true,
  capAutoBreakAtThreshold: false,
  breakReminderTime: "15:00",
  dayCloseTime: "00:05",
};

const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export interface RuleIssue {
  field: keyof TimeRules | "rules";
  message: string;
}

/** Prüft und ergänzt ein gespeichertes Regelwerk. Unbekannte Felder werden ignoriert. */
export function parseRules(raw: unknown): { rules: TimeRules; issues: RuleIssue[] } {
  const issues: RuleIssue[] = [];
  const rules: TimeRules = { ...DEFAULT_RULES };
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return { rules, issues: [{ field: "rules", message: "Regelwerk ist kein Objekt" }] };
  }
  const input = raw as Record<string, unknown>;

  const int = (field: keyof TimeRules, min: number, max: number) => {
    const value = input[field];
    if (value === undefined) return;
    if (typeof value !== "number" || !Number.isInteger(value) || value < min || value > max) {
      issues.push({ field, message: `muss eine ganze Zahl zwischen ${min} und ${max} sein` });
      return;
    }
    (rules[field] as number) = value;
  };
  const bool = (field: keyof TimeRules) => {
    const value = input[field];
    if (value === undefined) return;
    if (typeof value !== "boolean") {
      issues.push({ field, message: "muss ja/nein sein" });
      return;
    }
    (rules[field] as boolean) = value;
  };
  const time = (field: keyof TimeRules) => {
    const value = input[field];
    if (value === undefined) return;
    if (typeof value !== "string" || !TIME_RE.test(value)) {
      issues.push({ field, message: "muss eine Uhrzeit HH:MM sein" });
      return;
    }
    (rules[field] as string) = value;
  };

  int("breakThresholdMinutes", 60, 720);
  int("requiredBreakMinutes", 0, 180);
  int("minBreakSegmentMinutes", 0, 60);
  int("duplicateWindowSeconds", 0, 600);
  bool("autoBreakDeduction");
  bool("capAutoBreakAtThreshold");
  time("breakReminderTime");
  time("dayCloseTime");

  return { rules, issues };
}

export interface RuleSetVersion {
  validFrom: LocalDate;
  rules: unknown;
}

/** Die für einen Tag gültige Version — oder die Standardwerte, wenn keine passt. */
export function rulesForDay(versions: readonly RuleSetVersion[], day: LocalDate): TimeRules {
  let best: RuleSetVersion | undefined;
  for (const version of versions) {
    if (!isLocalDate(version.validFrom) || version.validFrom > day) continue;
    if (!best || version.validFrom > best.validFrom) best = version;
  }
  return best ? parseRules(best.rules).rules : { ...DEFAULT_RULES };
}
